import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ConsentBanner from "@/components/ConsentBanner";
import PageBoundary from "@/components/PageBoundary";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Videos from "@/pages/Videos";
import VideoDetail from "@/pages/VideoDetail";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import CoursePlayer from "@/pages/CoursePlayer";
import { Coaching, Partnership, Resources, Events } from "@/pages/InfoPages";
import Store from "@/pages/Store";
import Podcast from "@/pages/Podcast";
import { Blog, BlogPost } from "@/pages/Blog";
import Contact from "@/pages/Contact";
import Landing from "@/pages/Landing";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/AuthPages";
import { PaymentSuccess, PaymentCancel, NotFound } from "@/pages/Misc";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/admin/Admin";

const ScrollTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    let n = 0;
    const t = setInterval(() => {
      const el = document.getElementById(hash.slice(1));
      if (el || ++n > 20) { clearInterval(t); el?.scrollIntoView({ behavior: "smooth" }); }
    }, 150);
    return () => clearInterval(t);
  }, [pathname, hash]);
  return null;
};

const Loading = () => <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;

export const Protected = ({ staff, children }) => {
  const { user, isStaff } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (staff && !isStaff) return <Navigate to="/dashboard" replace />;
  return children;
};

const SiteLayout = () => (
  <>
    <Navbar />
    <main className="min-h-screen xl:pt-[52px]"><PageBoundary><Outlet /></PageBoundary></main>
    <Footer />
  </>
);

function App() {
  return (
    <BrowserRouter>
      <SiteProvider>
        <AuthProvider>
          <ScrollTop />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/videos" element={<Videos />} />
              <Route path="/videos/:slug" element={<VideoDetail />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:slug" element={<CourseDetail />} />
              <Route path="/coaching" element={<Coaching />} />
              <Route path="/partnership" element={<Partnership />} />
              <Route path="/membership" element={<Navigate to="/partnership" replace />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/store" element={<Store />} />
              <Route path="/podcast" element={<Podcast />} />
              <Route path="/events" element={<Events />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/lp/:slug" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/dashboard/*" element={<Protected><Dashboard /></Protected>} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/learn/:slug" element={<Protected><CoursePlayer /></Protected>} />
            <Route path="/admin/*" element={<Protected staff><Admin /></Protected>} />
          </Routes>
          <ConsentBanner />
          <Toaster theme="dark" position="top-center" />
        </AuthProvider>
      </SiteProvider>
    </BrowserRouter>
  );
}

export default App;
